import { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from './api/client';
import { useUserStore } from './store/userStore';
import Home from './pages/Home';
import SessionDetail from './pages/SessionDetail';
import CinemaDetail from './pages/CinemaDetail';
import SeatMap from './pages/SeatMap';
import Payment from './pages/Payment';
import MyTickets from './pages/MyTickets';
import AdminHome from './pages/admin/AdminHome';
import CinemasList from './pages/admin/CinemasList';
import CinemaForm from './pages/admin/CinemaForm';
import SeatEditor from './pages/admin/SeatEditor';
import SessionsList from './pages/admin/SessionsList';
import SessionForm from './pages/admin/SessionForm';
import TicketsList from './pages/admin/TicketsList';
import Scanner from './pages/admin/Scanner';
import Broadcast from './pages/admin/Broadcast';
import Banners from './pages/admin/Banners';
import PaymentCards from './pages/admin/PaymentCards';
import RequireAdmin from './components/RequireAdmin';
import Splash from './components/Splash';

export default function App() {
  const navigate = useNavigate();
  const setUser = useUserStore((s) => s.setUser);

  // Validate initData and pull role + active order pointer
  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const { data } = await api.get('/api/me');
      return data as { tg_id: number; username?: string; is_admin: boolean; admins_loaded?: number; lang: 'ru' | 'uz'; active_order_id?: number | null };
    },
  });

  useEffect(() => {
    if (data) {
      setUser({ tgId: data.tg_id, username: data.username, isAdmin: data.is_admin, adminsLoaded: data.admins_loaded, activeOrderId: data.active_order_id ?? null, lang: data.lang });
      // Note: we deliberately do NOT force-redirect to /payment/X anymore. A
      // HELD order is surfaced via a banner on Home and a "Перейти к оплате"
      // button in My Tickets — letting the user keep browsing the афиша.
    }
  }, [data, setUser, navigate]);

  if (isLoading) return <Splash />;

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/session/:id" element={<SessionDetail />} />
      <Route path="/session/:id/seats" element={<SeatMap />} />
      <Route path="/cinema/:id" element={<CinemaDetail />} />
      <Route path="/payment/:orderId" element={<Payment />} />
      <Route path="/my-tickets" element={<MyTickets />} />

      <Route path="/admin" element={<RequireAdmin><AdminHome /></RequireAdmin>} />
      <Route path="/admin/cinemas" element={<RequireAdmin><CinemasList /></RequireAdmin>} />
      <Route path="/admin/cinemas/new" element={<RequireAdmin><CinemaForm /></RequireAdmin>} />
      <Route path="/admin/cinemas/:id/edit" element={<RequireAdmin><CinemaForm /></RequireAdmin>} />
      <Route path="/admin/cinemas/:id/seats" element={<RequireAdmin><SeatEditor /></RequireAdmin>} />
      <Route path="/admin/sessions" element={<RequireAdmin><SessionsList /></RequireAdmin>} />
      <Route path="/admin/sessions/new" element={<RequireAdmin><SessionForm /></RequireAdmin>} />
      <Route path="/admin/sessions/:id/edit" element={<RequireAdmin><SessionForm /></RequireAdmin>} />
      <Route path="/admin/tickets" element={<RequireAdmin><TicketsList /></RequireAdmin>} />
      <Route path="/admin/scanner" element={<RequireAdmin><Scanner /></RequireAdmin>} />
      <Route path="/admin/broadcast" element={<RequireAdmin><Broadcast /></RequireAdmin>} />
      <Route path="/admin/banners" element={<RequireAdmin><Banners /></RequireAdmin>} />
      <Route path="/admin/payment-cards" element={<RequireAdmin><PaymentCards /></RequireAdmin>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
